import { useState } from "react";
import { C, getFontFamily } from "../constants/colors";

/*
 * SessionFeedback — inline thumbs up/down after AI chat solves a problem
 * Shows below the solution. Saves to localStorage (sync to Firestore later).
 *
 * Props:
 *   topic: string (the problem being discussed)
 *   lang: "en" | "hi"
 */
export function SessionFeedback({ topic, lang = "en" }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const [rating, setRating] = useState(null); // "up" | "down" | null
  const [saved, setSaved] = useState(false);

  const handleRate = (r) => {
    setRating(r);
    setSaved(true);
    // Save to localStorage
    try {
      const key = "samajh-session-feedback";
      const existing = JSON.parse(localStorage.getItem(key) || "[]");
      existing.push({
        topic: (topic || "").slice(0, 100),
        rating: r,
        timestamp: new Date().toISOString(),
      });
      // Keep last 50 feedbacks
      if (existing.length > 50) existing.splice(0, existing.length - 50);
      localStorage.setItem(key, JSON.stringify(existing));
    } catch {}
  };

  if (saved) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "6px 14px" }}>
        <div style={{
          fontSize: 12, color: "#1A7A52", fontWeight: 600, fontFamily: ff,
          background: "#E8F5EE", padding: "6px 14px", borderRadius: 10,
        }}>
          {isHi ? "धन्यवाद!" : "Thanks for your feedback!"}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", justifyContent: "center", gap: 10, padding: "6px 14px" }}>
      <div style={{ fontSize: 11, color: C.textMute, fontFamily: ff, fontWeight: 500, display: "flex", alignItems: "center" }}>
        {isHi ? "सहायक था?" : "Was this helpful?"}
      </div>
      <div onClick={() => handleRate("up")} style={{
        width: 36, height: 36, borderRadius: 10,
        background: rating === "up" ? "#E8F5EE" : "white",
        border: `1.5px solid ${rating === "up" ? "#1A7A52" : "#E8EFF5"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer",
        // WebView-compensated shadow
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={rating === "up" ? "#1A7A52" : "#6A808C"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/>
        </svg>
      </div>
      <div onClick={() => handleRate("down")} style={{
        width: 36, height: 36, borderRadius: 10,
        background: rating === "down" ? "#FEE8E8" : "white",
        border: `1.5px solid ${rating === "down" ? "#CC4444" : "#E8EFF5"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={rating === "down" ? "#CC4444" : "#6A808C"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z"/><path d="M17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/>
        </svg>
      </div>
    </div>
  );
}

/*
 * FeedbackForm — full feedback form for Settings page
 *
 * Props:
 *   lang: "en" | "hi"
 *   onClose: () => void
 */
export function FeedbackForm({ lang = "en", onClose }) {
  const ff = getFontFamily(lang);
  const isHi = lang === "hi";
  const [category, setCategory] = useState("");
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const categories = isHi
    ? ["बग / गड़बड़ी", "सुझाव", "सवाल गलत था", "AI ने सही guide नहीं किया", "अन्य"]
    : ["Bug / Issue", "Suggestion", "Wrong question", "AI didn't guide well", "Other"];

  const handleSubmit = () => {
    if (!text.trim()) return;
    try {
      const key = "samajh-feedback-forms";
      const existing = JSON.parse(localStorage.getItem(key) || "[]");
      existing.push({
        category,
        text: text.trim().slice(0, 500),
        timestamp: new Date().toISOString(),
      });
      if (existing.length > 20) existing.splice(0, existing.length - 20);
      localStorage.setItem(key, JSON.stringify(existing));
    } catch {}
    setSubmitted(true);
    setTimeout(() => onClose?.(), 2000);
  };

  if (submitted) {
    return (
      <div style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(10,24,32,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: ff,
      }}>
        <div style={{
          background: "white", borderRadius: 20, padding: "32px 24px",
          maxWidth: 320, width: "90%", textAlign: "center",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1A7A52" strokeWidth="2" strokeLinecap="round" style={{ marginBottom: 16 }}>
            <circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/>
          </svg>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0A1820", marginBottom: 6 }}>
            {isHi ? "धन्यवाद!" : "Thank you!"}
          </div>
          <div style={{ fontSize: 13, color: "#6A808C" }}>
            {isHi ? "आपकी feedback हमारे लिए ज़रूरी है" : "Your feedback helps us improve"}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(10,24,32,0.6)",
      backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      fontFamily: ff,
    }} onClick={onClose}>
      <div style={{
        width: "100%", maxWidth: 420,
        background: "white", borderRadius: "24px 24px 0 0",
        padding: "24px 20px 32px",
        boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
      }} onClick={e => e.stopPropagation()}>
        {/* Drag handle */}
        <div style={{ width: 40, height: 4, borderRadius: 2, background: "#DAE4EC", margin: "0 auto 18px" }} />

        <div style={{ fontSize: 18, fontWeight: 700, color: "#0A1820", marginBottom: 4 }}>
          {isHi ? "Feedback दें" : "Give Feedback"}
        </div>
        <div style={{ fontSize: 12, color: "#6A808C", marginBottom: 16 }}>
          {isHi ? "हम सुन रहे हैं — बताएं क्या बेहतर हो सकता है" : "We're listening — tell us what can be better"}
        </div>

        {/* Category pills */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {categories.map((c, i) => (
            <div key={i} onClick={() => setCategory(c)} style={{
              padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600,
              cursor: "pointer", transition: "all 0.2s",
              background: category === c ? "#E8F5EE" : "#F5F9FB",
              color: category === c ? "#1A7A52" : "#3A5464",
              border: `1.5px solid ${category === c ? "#1A7A52" : "#E8EFF5"}`,
            }}>
              {c}
            </div>
          ))}
        </div>

        {/* Text input */}
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={isHi ? "यहाँ लिखें..." : "Write here..."}
          rows={3}
          style={{
            width: "100%", padding: "12px 14px", borderRadius: 12,
            border: "1.5px solid #E8EFF5", fontSize: 14,
            fontFamily: ff, color: "#0A1820", background: "#F8FAFB",
            outline: "none", resize: "none", lineHeight: 1.6,
          }}
        />

        {/* Submit */}
        <button onClick={handleSubmit} disabled={!text.trim()} style={{
          width: "100%", padding: "14px", marginTop: 14,
          background: text.trim() ? "#1A7A52" : "#D8E4EE",
          color: text.trim() ? "white" : "#8A9AA4",
          border: "none", borderRadius: 14, fontSize: 15, fontWeight: 700,
          cursor: text.trim() ? "pointer" : "default", fontFamily: ff,
          boxShadow: text.trim() ? "0 4px 12px rgba(26,122,82,0.25)" : "none",
        }}>
          {isHi ? "भेजें" : "Submit"}
        </button>

        <div onClick={onClose} style={{
          textAlign: "center", marginTop: 12, fontSize: 13,
          color: "#6A808C", cursor: "pointer", fontWeight: 500,
        }}>
          {isHi ? "रहने दो" : "Not now"}
        </div>
      </div>
    </div>
  );
}
